import { Box, CircularProgress } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { selectActiveCompanyId, selectRole } from "src/redux/features/user/selectors/userState.selectors";
import { useAppSelector } from "src/redux/hooks/redux";
import { IRoles } from "src/utils";
import { useSocket } from "../../../context/SocketContext";
import { useMessageSocket, useMessageReadSocket } from "../../../hooks/useMessageSockets";
import { Composer } from "../components/Composer/Composer";
import { DropOverlay } from "../components/DropOverlay";
import { ImagePreviewModal } from "../components/ImagePreviewModal";
import { MessagesList } from "../components/MessageList";
import { MessageSearchBar } from "../components/MessageSearchBar";
import { MessageSearchResultList } from "../components/MessageSearchResultList";
import { useMessageComposer } from "../hooks/useMessageComposer";
import { useMessageFileDrop } from "../hooks/useMessageFileDrop";
import { useMessagesScroll } from "../hooks/useMessageScroll";
import { useMessagesMutation } from "../hooks/useMessagesMutation";
import { useMessageSearch } from "../hooks/useMessageSearch";
import { useMessagesQuery } from "../hooks/useMessagesQuery";
import { getMessageQueryKey } from "../utils/messageConversation.utils";
import { buildChatDraftKey } from "../../chat-dock/chatDrafts";

const buildMessageReceiver = (contact) => {
  if (!contact) return null;

  if (contact.type === "company") {
    return { id: contact.id, type: "company" };
  }

  if (contact.type === "organizationCoworker") {
    return { uid: contact.uid, type: "user", companyId: contact?.companyId };
  }

  return { uid: contact.uid, type: "workforce" };
};

const buildMessageSender = (contact, { uid, company, workforces, currentRole }) => {
  const roleType = company?.role?.type;

  if (currentRole === IRoles.Employee) {
    const workforceUid = workforces?.find(
      (w) => Number(w.companyId) === Number(contact?.companyId ?? contact?.id),
    )?.uid;
    return { uid: workforceUid, type: "workforce" };
  }

  if (currentRole === IRoles.Business || currentRole === IRoles.Admin || currentRole === IRoles.Manager || currentRole === IRoles.VR || currentRole === IRoles.SuperAdmin) {
    return { uid, type: "user" };
  }

  if (currentRole === IRoles.Other) {
    return { uid, type: "user" }
  }

  return null;
};

export const MessagesView = ({
  selectedContact,
  focusMessageUid = null,
  focusMessageText = null,
  focusMessageRequestKey = null,
  onFocusMessageConsumed,
  search: externalSearch = null,
}) => {
  const queryClient = useQueryClient();
  const { uid, company, workforces } = useSelector((state) => state.userState);
  const { socket } = useSocket();

  const currentRole = useAppSelector(selectRole);
  const activeCompanyId = useAppSelector(selectActiveCompanyId);

  const [imagePreview, setImagePreview] = useState({ open: false, url: null, name: null });

  const isAmazonConnect = selectedContact?.id === "amazonconnect";
  const CCP_URL = "https://dev-backend.flownect.com/amazon-connect/ccp-embed";

  const ownSearch = useMessageSearch();
  const search = externalSearch ?? ownSearch;
  const { debouncedSearchText, isSearching: isSearchingMessages, selectedFocus } = search;

  const receiver = useMemo(() => buildMessageReceiver(selectedContact), [selectedContact]);
  const sender = useMemo(() => buildMessageSender(selectedContact, { uid, company, workforces, currentRole }), [selectedContact, uid, company, workforces, currentRole]);
  const employeeCompanyDisplayName =
    currentRole === IRoles.Employee && selectedContact?.type === "company"
      ? selectedContact?.businessName || company?.businessName || "Company"
      : undefined;

  const { messagesQuery } = useMessagesQuery({ receiver, companyId: activeCompanyId, enabled: !isAmazonConnect && Boolean(receiver) });
  const { messagesQuery: searchMessagesQuery } = useMessagesQuery({
    receiver,
    companyId: activeCompanyId,
    search: debouncedSearchText,
    enabled: isSearchingMessages && !isAmazonConnect && Boolean(receiver),
  });
  const { messagesMutation } = useMessagesMutation();
  const { messagesContainerRef, scrollToBottom, handleScroll } = useMessagesScroll(messagesQuery);

  const handleMessageSent = useCallback(() => {
    if (!receiver) return;
    queryClient.invalidateQueries({ queryKey: getMessageQueryKey(receiver, activeCompanyId) });
  }, [queryClient, receiver, activeCompanyId]);

  useMessageSocket(socket, receiver, scrollToBottom, { workforces, self: sender, companyId: activeCompanyId });
  useMessageReadSocket(socket, receiver, sender, { workforces, companyId: activeCompanyId });

  const draftKey = useMemo(
    () =>
      buildChatDraftKey({
        companyId: activeCompanyId,
        scope: "internal",
        subType: receiver?.type,
        id: receiver?.id ?? receiver?.uid,
      }),
    [receiver, activeCompanyId],
  );

  const composer = useMessageComposer({
    messagesMutation,
    receiver,
    onMessageSent: handleMessageSent,
    draftKey,
  });
  const { isDragging, handleDragEnter, handleDragLeave, handleDragOver, handleDrop } = useMessageFileDrop((files) => {
    composer.handleDragAndDropImages(files);
    composer.handleDragAndDropFiles(files);
  });

  const handleOpenImagePreview = (url, name) => setImagePreview({ open: true, url, name });
  const handleCloseImagePreview = () => setImagePreview({ open: false, url: null, name: null });

  const searchResults = useMemo(
    () => searchMessagesQuery?.data?.pages?.flatMap((page) => page?.items ?? []) ?? [],
    [searchMessagesQuery?.data],
  );

  const handleFocusConsumed = useCallback(() => {
    search.consumeFocus();
    onFocusMessageConsumed?.();
  }, [search, onFocusMessageConsumed]);

  const effectiveFocusUid = selectedFocus?.uid ?? focusMessageUid;
  const effectiveFocusText = selectedFocus ? null : focusMessageText;
  const effectiveFocusRequestKey = selectedFocus?.requestKey ?? focusMessageRequestKey;

  if (isAmazonConnect) {
    return (
      <Box sx={{ flex: 1, position: "relative" }}>
        <iframe src={CCP_URL} title="Amazon Connect CCP" style={{ width: "100%", height: "100%", border: 0 }} allow="microphone; camera; autoplay" />
      </Box>
    );
  }

  if (messagesQuery?.isLoading)
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%" }}>
        <CircularProgress />
      </Box>
    );

  if (messagesQuery?.isError)
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%", color: "error.main", fontWeight: 600 }}>
        {messagesQuery?.error?.message || "Error loading messages"}
      </Box>
    );

  return (
    <Box
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
      sx={{
        position: "relative",
        display: "flex",
        flexDirection: "column",
        height: "100%",
        overflow: "hidden",
        backgroundColor: "background.default",
      }}
    >
      {isDragging && <DropOverlay />}

      {!externalSearch && (
        <MessageSearchBar
          searchOpen={search.searchOpen}
          searchText={search.searchText}
          inputRef={search.inputRef}
          onOpen={search.open}
          onClose={search.close}
          onChange={search.setSearchText}
          onClear={search.clear}
        />
      )}

      {isSearchingMessages ? (
        <MessageSearchResultList
          messages={searchResults}
          searchQuery={debouncedSearchText}
          self={sender}
          isLoading={searchMessagesQuery.isLoading}
          error={searchMessagesQuery.error}
          isFetchingNextPage={searchMessagesQuery.isFetchingNextPage}
          hasNextPage={searchMessagesQuery.hasNextPage}
          onFetchNextPage={searchMessagesQuery.fetchNextPage}
          onSelectMessage={search.selectResult}
        />
      ) : (
        <MessagesList
          handleScroll={handleScroll}
          messagesContainerRef={messagesContainerRef}
          messagesQuery={messagesQuery}
          sender={sender}
          onOpenImagePreview={handleOpenImagePreview}
          maskNonOwnSenderName={employeeCompanyDisplayName}
          pendingMessages={composer.pendingMessages}
          onRetryMessage={composer.retryPendingMessage}
          focusMessageUid={effectiveFocusUid}
          focusMessageText={effectiveFocusText}
          focusMessageRequestKey={effectiveFocusRequestKey}
          onFocusMessageConsumed={handleFocusConsumed}
        />
      )}

      {!isSearchingMessages && <Composer composer={composer} />}
      <ImagePreviewModal open={imagePreview.open} onClose={handleCloseImagePreview} imageUrl={imagePreview.url} imageName={imagePreview.name} />
    </Box>
  );
};
